import axios from 'axios';
import toast from 'react-hot-toast';
import type {
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  ForgotPasswordRequest,
  TokenResponse,
  UserResponse,
  ApiResponse,
  TokenValidationResponse,
  PublicKeyResponse,
  PaginatedUsersResponse,
  UpdateProfileRequest,
  ChangePasswordRequest,
  SetPasswordRequest,
  ResumeRequest,
  Resume,
  ResumeAnalytics,
  SaveResponse,
  ParserResponse,
  UploadResponse,
  EnrichResponse,
  MatchResponse,
  EnrichedData,
  ParsedData,
  CoverLetterResponse,
  AtsScoreResponse,
  ReferralInfo,
  ClaimReferralResponse,
  AdminTemplate,
  ApplicationBoard,
  ApplicationSummary,
  ApplicationDetail,
  ApplicationPayload,
  ApplicationStatus,
  Interview,
  InterviewPayload,
  Organization,
  OrganizationPayload,
  AiInterview,
  AiInterviewPayload,
  AiInterviewResult,
  AiInterviewSession,
  AiInterviewTranscriptLine,
} from '../types';

const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:8081/api/v1/auth';
const RESUME_API_URL = import.meta.env.VITE_RESUME_API_URL || 'http://localhost:8080/api/v1/resumes';
const PARSER_API_URL = import.meta.env.VITE_PARSER_API_URL || 'http://localhost:8000';
const BILLING_API_URL = import.meta.env.VITE_BILLING_API_URL || 'http://localhost:8083/api/v1/billing';

let isRedirecting = false;

const handleSessionExpiry = () => {
  if (isRedirecting) return;
  isRedirecting = true;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/login';
};

export { handleSessionExpiry };

const parserApi = axios.create({
  baseURL: PARSER_API_URL,
});

const authApi = axios.create({
  baseURL: AUTH_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const resumeApi = axios.create({
  baseURL: RESUME_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const billingApi = axios.create({
  baseURL: BILLING_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests (skip for login/register)
authApi.interceptors.request.use((config) => {
  const publicEndpoints = ['/login', '/register', '/refresh', '/public-key', '/oauth/callback'];
  const isPublic = publicEndpoints.some(ep => config.url?.includes(ep));
  
  if (!isPublic) {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

resumeApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ActivityData {
  event: string;
  feature?: string;
  path?: string;
  success?: boolean;
  error?: string;
  metadata?: Record<string, string>;
}

export interface ActivityLogItem {
  id: number;
  userId: string;
  event: string;
  feature?: string | null;
  path?: string | null;
  success: boolean;
  error?: string | null;
  metadata?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdAt: string;
}

export interface ActivityLogPage {
  content: ActivityLogItem[];
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

export interface ActivitySummary {
  totalEvents: number;
  totalErrors: number;
  distinctUsers: number;
  successRate: number;
  daily: Array<{ date: string; count: number }>;
  byFeature: Array<{ feature: string | null; success: boolean; count: number }>;
  byUserAndEvent: Array<{ userId: string; event: string; count: number }>;
  recentErrors: ActivityLogItem[];
}

export interface AppSetting {
  key: string;
  value: string;
  isSecret: boolean;
  updatedAt: string | null;
}

export const trackActivity = (data: ActivityData): void => {
  if (!localStorage.getItem('access_token')) return;
  resumeApi.post('/activities', data).catch(() => {});
};

resumeApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) handleSessionExpiry();
    const url = error.config?.url || '';
    if (!url.includes('/activities')) {
      trackActivity({
        event: 'api_error',
        feature: 'api',
        path: url,
        success: false,
        error: error.response?.status ? `HTTP ${error.response.status}` : (error.message || 'Request failed'),
      });
    }
    return Promise.reject(error);
  }
);

billingApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

billingApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) handleSessionExpiry();
    return Promise.reject(error);
  }
);

parserApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) handleSessionExpiry();
    return Promise.reject(error);
  }
);

// Handle token refresh on 401
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const publicEndpoints = ['/login', '/register', '/refresh', '/public-key', '/oauth/callback'];
    const isPublic = publicEndpoints.some(ep => originalRequest?.url?.includes(ep));

    if (error.response?.status === 401 && !originalRequest._retry && !isPublic) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return authApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        handleSessionExpiry();
        return Promise.reject(error);
      }

      try {
        const response = await authApi.post<ApiResponse<TokenResponse>>('/refresh', {
          refresh_token: refreshToken,
        });
        const { access_token, refresh_token } = response.data.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        processQueue(null, access_token);
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return authApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        handleSessionExpiry();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authService = {
  register: (data: RegisterRequest) =>
    authApi.post<ApiResponse<RegisterResponse>>('/register', {
      ...data,
      refCode: data.refCode ?? localStorage.getItem(REFERRAL_CODE_KEY) ?? undefined,
    }),

  login: (data: LoginRequest) =>
    authApi.post<ApiResponse<TokenResponse>>('/login', data),

  forgotPassword: (data: ForgotPasswordRequest) =>
    authApi.post<ApiResponse<void>>('/forgot-password', data),

  resendVerification: (username: string) =>
    authApi.post<ApiResponse<void>>('/resend-verification', { username }),

  refreshToken: (refreshToken: string) =>
    authApi.post<ApiResponse<TokenResponse>>('/refresh', { refresh_token: refreshToken }),

  logout: () => {
    const token = localStorage.getItem('access_token');
    return authApi.post('/logout', {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  getProfile: () =>
    authApi.get<ApiResponse<UserResponse>>('/profile'),

  updateProfile: (data: UpdateProfileRequest) =>
    authApi.put<ApiResponse<UserResponse>>('/profile', data),

  changePassword: (data: ChangePasswordRequest) =>
    authApi.post<ApiResponse<void>>('/profile/change-password', data),

  setPassword: (data: SetPasswordRequest) =>
    authApi.post<ApiResponse<void>>('/profile/set-password', data),

  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return authApi.post<ApiResponse<UserResponse>>('/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  removeAvatar: () =>
    authApi.delete<ApiResponse<UserResponse>>('/profile/avatar'),

  validateToken: (token: string) =>
    authApi.post<ApiResponse<TokenValidationResponse>>('/validate', { token }),

  getPublicKey: () =>
    authApi.get<ApiResponse<PublicKeyResponse>>('/public-key'),

  oauthCallback: (code: string, redirectUri: string) =>
    authApi.post<ApiResponse<TokenResponse>>('/oauth/callback', { code, redirectUri }),
};

export interface TemplateItem {
  id: string;
  name: string;
  category: string;
  free: boolean;
}

export interface TemplatePage {
  templates: TemplateItem[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

// Admin API
export const adminService = {
  listUsers: (page = 0, size = 10, search?: string) =>
    authApi.get<ApiResponse<PaginatedUsersResponse>>('/admin/users', {
      params: { page, size, search },
    }),

  assignRole: (userId: string, roleName: string) =>
    authApi.post<ApiResponse<void>>(`/admin/users/${userId}/roles`, { roleName }),

  removeRole: (userId: string, roleName: string) =>
    authApi.delete<ApiResponse<void>>(`/admin/users/${userId}/roles/${roleName}`),

  setUserStatus: (userId: string, enabled: boolean) =>
    authApi.patch<ApiResponse<void>>(`/admin/users/${userId}/status`, { enabled }),

  deleteUser: (userId: string) =>
    authApi.delete<ApiResponse<void>>(`/admin/users/${userId}`),

  getSettings: () =>
    billingApi.get<AppSetting[]>('/admin/settings'),

  updateSettings: (values: Record<string, string>) =>
    billingApi.put<AppSetting[]>('/admin/settings', values),

  getTemplates: () =>
    resumeApi.get<AdminTemplate[]>('/admin/templates'),

  getTemplateById: (id: string) =>
    resumeApi.get<AdminTemplate>(`/admin/templates/${id}`),

  updateTemplate: (id: string, data: Partial<AdminTemplate>) =>
    resumeApi.put<AdminTemplate>(`/admin/templates/${id}`, data),

  createTemplate: (data: { id: string; name: string; category: string; content: string; isFree?: boolean; isEnabled?: boolean; sortOrder?: number }) =>
    resumeApi.post<AdminTemplate>('/admin/templates', data),

  deleteTemplate: (id: string) =>
    resumeApi.delete(`/admin/templates/${id}`),

  previewTemplateContent: (content: string) =>
    resumeApi.post<string>('/admin/templates/preview', { content }),
};

export function toServerFormat(data: ResumeRequest, filterEmpty = true): Record<string, unknown> {
  const result = {
    templateId: data.templateId,
    personalInfo: {
      fullName: data.personalInfo.fullName,
      email: data.personalInfo.email,
      phone: data.personalInfo.phone,
      address: data.personalInfo.address,
      linkedIn: data.personalInfo.linkedin || null,
      website: data.personalInfo.website,
      photo: data.personalInfo.photo || null,
    },
    summary: data.summary || '',
    experience: (filterEmpty
      ? data.experience.filter(exp => exp.company?.trim() && exp.role?.trim())
      : data.experience
    ).map(exp => ({
      company: exp.company,
      role: exp.role,
      startDate: exp.startDate || '',
      endDate: exp.endDate || '',
      currentJob: exp.current,
      description: exp.description || '',
    })),
    education: (filterEmpty
      ? data.education.filter(edu => edu.institution?.trim() && edu.degree?.trim())
      : data.education
    ).map(edu => ({
      institution: edu.institution,
      degree: edu.degree,
      fieldOfStudy: edu.fieldOfStudy,
      startDate: edu.startDate || '',
      endDate: edu.endDate || '',
      year: edu.year || edu.endDate || edu.startDate || '',
      gpa: edu.gpa || '',
    })),
    skills: data.skills,
  };
  return result;
}

export function fromServerFormat(data: Record<string, unknown>): ResumeRequest {
  const personalInfo = (data.personalInfo ?? {}) as Record<string, unknown>;
  const experience = ((data.experience ?? []) as Record<string, unknown>[]).map(exp => ({
    company: (exp.company as string) || '',
    role: (exp.role as string) || '',
    startDate: (exp.startDate as string) || '',
    endDate: (exp.endDate as string) || '',
    current: Boolean(exp.currentJob),
    description: (exp.description as string) || '',
  }));
  const education = ((data.education ?? []) as Record<string, unknown>[]).map(edu => {
    const rawStart = (edu.startDate as string) || '';
    const rawEnd = (edu.endDate as string) || '';
    const rawYear = (edu.year as string) || '';
    const startDate = rawStart;
    const endDate = rawEnd || rawYear;
    const year = rawYear || rawEnd || rawStart;
    return {
      institution: (edu.institution as string) || '',
      degree: (edu.degree as string) || '',
      fieldOfStudy: (edu.fieldOfStudy as string) || '',
      startDate,
      endDate,
      current: false,
      gpa: (edu.gpa as string) || '',
      year,
    };
  });
  return {
    templateId: (data.templateId as string) || '',
    personalInfo: {
      fullName: (personalInfo.fullName as string) || '',
      email: (personalInfo.email as string) || '',
      phone: (personalInfo.phone as string) || '',
      address: (personalInfo.address as string) || '',
      linkedin: (personalInfo.linkedIn as string) || '',
      website: (personalInfo.website as string) || '',
      photo: (personalInfo.photo as string) || '',
    },
    summary: (data.summary as string) || '',
    experience,
    education,
    skills: ((data.skills as string[]) ?? []),
  };
}

export async function getBlobErrorMessage(error: unknown): Promise<string> {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  if (!response) return 'Network error. Please check your connection.';
  const status = response.status ?? 0;
  let message = `Request failed (${status})`;
  const data = response.data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      message = formatApiError(status, parsed);
    } catch {
      message = data || message;
    }
  } else if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      message = formatApiError(status, parsed);
    } catch {
      message = `Request failed (${status})`;
    }
  } else if (data && typeof data === 'object') {
    message = formatApiError(status, data as Record<string, unknown>);
  }
  return message;
}

function formatApiError(status: number, parsed: Record<string, unknown>): string {
  if (status === 400 && typeof parsed.message === 'string' && parsed.message === 'Validation failed') {
    const errors = parsed.errors as Record<string, string> | undefined;
    if (errors && typeof errors === 'object') {
      const lines = Object.values(errors);
      return lines.length ? `Please fix: ${lines.join(' ')}` : parsed.message;
    }
  }
  return typeof parsed.message === 'string' && parsed.message
    ? parsed.message
    : `Request failed (${status})`;
}

// Resume Generator API
export const resumeService = {
  generateResume: (data: ResumeRequest) =>
    resumeApi.post<Blob>('/generate', toServerFormat(data), {
      responseType: 'blob',
    }),

  getAllResumes: () =>
    resumeApi.get<Resume[]>(''),

  getResumeById: (id: number) =>
    resumeApi.get<Resume>(`/${id}`),

  deleteResume: (id: number) =>
    resumeApi.delete(`/${id}`),

  getTemplates: (offset = 0, limit = 12) =>
    resumeApi.get<TemplatePage>('/templates', {
      params: { offset, limit }
    }),

  getSampleProfile: () =>
    resumeApi.get<ResumeRequest>('/sample'),

  getProfile: (userId: string) =>
    resumeApi.get<ResumeRequest>(`/profiles/${userId}`),

  saveProfile: (userId: string, data: ResumeRequest) =>
    resumeApi.put<ResumeRequest>(`/profiles/${userId}`, toServerFormat(data, false)),

  initializeProfile: (userId: string) =>
    resumeApi.post<ResumeRequest>(`/profiles/${userId}/initialize`),

  importLinkedInProfile: (userId: string) =>
    resumeApi.post<ResumeRequest>(`/profiles/${userId}/linkedin-import`),

  previewHtml: (data: ResumeRequest) =>
    resumeApi.post<string>('/preview', toServerFormat(data)),

  // Saved resumes already use server field names (linkedIn/currentJob), so post
  // them raw instead of running toServerFormat (which would null those fields).
  previewSavedResume: (data: ResumeRequest) =>
    resumeApi.post<string>('/preview', {
      templateId: data.templateId,
      personalInfo: data.personalInfo,
      summary: data.summary || '',
      experience: data.experience,
      education: data.education,
      skills: data.skills,
    }),

  saveResume: (data: ResumeRequest) =>
    resumeApi.post<SaveResponse>('/save', toServerFormat(data)),

  getAdminActivityLogs: (page = 0, size = 50, event?: string, feature?: string, success?: boolean) =>
    resumeApi.get<ActivityLogPage>('/activities', {
      params: {
        page,
        size,
        ...(event ? { event } : {}),
        ...(feature ? { feature } : {}),
        ...(success !== undefined ? { success } : {}),
      },
    }),

  getActivitySummary: () =>
    resumeApi.get<ActivitySummary>('/activities/summary'),

  updatePublicStatus: (id: number, isPublic: boolean) =>
    resumeApi.put<Resume>(`/${id}/public-status`, { isPublic }),

  setDisabled: (id: number, disabled: boolean) =>
    resumeApi.put<Resume>(`/${id}/disable-status`, { disabled }),

  getResumeAnalytics: (id: number) =>
    resumeApi.get<ResumeAnalytics>(`/${id}/analytics`),

  health: () =>
    resumeApi.get('/health'),
};

// Public resume links (no auth). Raw fetch to avoid the resumeApi 401 -> redirect loop.
const RESUME_API_ORIGIN = RESUME_API_URL.replace(/\/api\/v1\/resumes$/, '');

export const publicResumeService = {
  htmlUrl: (slug: string) => `${RESUME_API_ORIGIN}/api/v1/public/resumes/${slug}`,
  pdfUrl: (slug: string) => `${RESUME_API_ORIGIN}/api/v1/public/resumes/${slug}/pdf`,

  fetchHtml: async (slug: string): Promise<string> => {
    const response = await fetch(publicResumeService.htmlUrl(slug));
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  },

  recordDownload: (slug: string): void => {
    fetch(`${RESUME_API_ORIGIN}/api/v1/public/resumes/${slug}/download-event`, { method: 'POST' }).catch(() => {});
  },
};

// Resume Parser API
export const parserService = {
  uploadAndParse: async (file: File): Promise<ParserResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await parserApi.post<ParserResponse>('/parse', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },

  uploadFile: async (file: File): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await parserApi.post<UploadResponse>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },

  parseFile: async (sessionId: string, apiKey: string): Promise<ParserResponse> => {
    const formData = new FormData();
    formData.append('api_key', apiKey);
    
    const response = await parserApi.post<ParserResponse>(`/parse/${sessionId}`, formData);
    return response.data;
  },

  health: () =>
    parserApi.get('/health'),
};

export const enrichService = {
  enrichContent: async (parsedData: ParsedData): Promise<EnrichResponse> => {
    const response = await parserApi.post('/enrich', { parsed_data: parsedData });
    return response.data;
  },
};

export const matcherService = {
  match: async (jdUrl: string, enrichedCV: EnrichedData): Promise<MatchResponse> => {
    const response = await parserApi.post('/match', {
      jd_url: jdUrl,
      enriched_cv: enrichedCV,
    });
    return response.data;
  },

  matchText: async (jdText: string, enrichedCV: EnrichedData): Promise<MatchResponse> => {
    const response = await parserApi.post('/match-text', {
      jd_text: jdText,
      enriched_cv: enrichedCV,
    });
    return response.data;
  },
};

export const coverLetterService = {
  generate: async (parsedData: ParsedData | EnrichedData, jobPosting: string): Promise<CoverLetterResponse> => {
    const response = await parserApi.post('/generate', {
      parsed_data: parsedData,
      job_posting: jobPosting,
    });
    return response.data;
  },
};

export const atsService = {
  score: async (
    resumeData: ResumeRequest | ParsedData,
    jobDescription?: string,
  ): Promise<AtsScoreResponse> => {
    const response = await parserApi.post<AtsScoreResponse>('/score', {
      resume_data: resumeData,
      job_description: jobDescription || null,
    });
    return response.data;
  },
};

export interface SubscriptionStatus {
  planId: string;
  status: string;
  isPro: boolean;
  currentPeriodEnd: string | null;
}

// Job Application Tracker (Kanban) API — served by resume-generator under /api/v1/resumes
export const applicationsService = {
  getBoard: () =>
    resumeApi.get<ApplicationBoard>('/applications/board'),

  getArchived: () =>
    resumeApi.get<ApplicationSummary[]>('/applications/archived'),

  setArchived: (id: number, archived: boolean) =>
    resumeApi.patch<ApplicationDetail>(`/applications/${id}/archive-status`, { archived }),

  archiveAll: (archived: boolean) =>
    resumeApi.patch<{ updated: number }>('/applications/archive-all', { archived }),

  list: (params?: { status?: ApplicationStatus; resumeId?: number; q?: string }) =>
    resumeApi.get<ApplicationSummary[]>('/applications', { params }),

  get: (id: number) =>
    resumeApi.get<ApplicationDetail>(`/applications/${id}`),

  create: (payload: ApplicationPayload) =>
    resumeApi.post<ApplicationDetail>('/applications', payload),

  update: (id: number, payload: ApplicationPayload) =>
    resumeApi.patch<ApplicationDetail>(`/applications/${id}`, payload),

  updateStatus: (id: number, status: ApplicationStatus) =>
    resumeApi.patch<ApplicationDetail>(`/applications/${id}/status`, { status }),

  remove: (id: number) =>
    resumeApi.delete(`/applications/${id}`),

  createInterview: (applicationId: number, payload: InterviewPayload) =>
    resumeApi.post<Interview>(`/applications/${applicationId}/interviews`, payload),

  updateInterview: (id: number, payload: InterviewPayload) =>
    resumeApi.patch<Interview>(`/interviews/${id}`, payload),

  deleteInterview: (id: number) =>
    resumeApi.delete(`/interviews/${id}`),

  downloadIcs: async (id: number): Promise<void> => {
    const response = await resumeApi.get(`/interviews/${id}/ics`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'text/calendar' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `interview-${id}.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

export const organizationsService = {
  list: (q?: string) =>
    resumeApi.get<Organization[]>('/organizations', { params: q ? { q } : {} }),

  create: (payload: OrganizationPayload) =>
    resumeApi.post<Organization>('/organizations', payload),
};

// AI Voice Interviews — served by resume-generator under /api/v1/resumes
export const aiInterviewsService = {
  listForApplication: (applicationId: number) =>
    resumeApi.get<AiInterview[]>(`/applications/${applicationId}/ai-interviews`),

  create: (applicationId: number, payload: AiInterviewPayload) =>
    resumeApi.post<AiInterview>(`/applications/${applicationId}/ai-interviews`, payload),

  get: (id: number) =>
    resumeApi.get<AiInterview>(`/ai-interviews/${id}`),

  remove: (id: number) =>
    resumeApi.delete(`/ai-interviews/${id}`),

  start: (id: number) =>
    resumeApi.post<AiInterview>(`/ai-interviews/${id}/start`),

  session: (id: number) =>
    resumeApi.post<AiInterviewSession>(`/ai-interviews/${id}/session`),

  appendTranscript: (id: number, lines: AiInterviewTranscriptLine[]) =>
    resumeApi.post<AiInterviewTranscriptLine[]>(`/ai-interviews/${id}/transcript`, { lines }),

  complete: (id: number, transcript: AiInterviewTranscriptLine[]) =>
    resumeApi.post<AiInterview>(`/ai-interviews/${id}/complete`, { transcript }),

  cancel: (id: number) =>
    resumeApi.post<AiInterview>(`/ai-interviews/${id}/cancel`),

  getResult: (id: number) =>
    resumeApi.get<AiInterviewResult>(`/ai-interviews/${id}/result`),
};

export interface Plan {
  id: string;
  name: string;
  amount: number;
  currency: string;
  interval: string;
}

export const billingService = {
  getSubscription: () =>
    billingApi.get<SubscriptionStatus>('/subscription'),

  createCheckout: (planId?: string, gateway?: string) =>
    billingApi.post<{ url: string }>('/checkout', null, {
      params: {
        planId: planId || 'pro-monthly',
        ...(gateway ? { gateway } : {}),
      },
    }),

  createPortal: () =>
    billingApi.post<{ url: string }>('/portal'),

  getPlans: (gateway?: string) =>
    billingApi.get<{ plans: Plan[]; gateway: string }>('/plans', {
      params: gateway ? { gateway } : {},
    }),

  isPaymentsEnabled: () =>
    billingApi.get<{ enabled: boolean }>('/payments-enabled'),
};

export const referralService = {
  getInfo: () =>
    billingApi.get<ReferralInfo>('/referral'),

  claim: (code: string) =>
    billingApi.post<ClaimReferralResponse>('/referral/claim', { code }),
};

export const REFERRAL_CODE_KEY = 'resume_ref_code';
export const LAST_TEMPLATE_KEY = 'resume_last_template';

export const getApiErrorMessage = (error: unknown, fallback = 'Something went wrong. Please try again.'): string => {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  if (data?.message) return data.message;
  return fallback;
};

export const claimPendingReferral = async (): Promise<void> => {
  const code = localStorage.getItem(REFERRAL_CODE_KEY);
  if (!code) return;
  try {
    await referralService.claim(code);
    localStorage.removeItem(REFERRAL_CODE_KEY);
    toast.success('Referral code applied!');
    } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 400) {
      // Deterministic rejection (self-referral / unknown code / already referred) — drop the key.
      const msg = (err as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
      toast.error(msg || 'Referral code could not be applied');
      localStorage.removeItem(REFERRAL_CODE_KEY);
    }
    // Other errors (network, 401, 5xx) — keep the key so it's retried on the next login/Profile visit.
  }
};

export { authApi, resumeApi, parserApi };
